
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface BookingStatus {
  id: string;
  status: 'pending' | 'confirmed' | 'assigned' | 'in-progress' | 'completed';
  from: string;
  to: string;
  date: string;
  time: string;
  vehicle: string;
  price: string;
  driver?: {
    name: string;
    phone: string;
    rating: number;
  };
}

const DEMO_BOOKING: BookingStatus = {
  id: 'VE-2025-4821',
  status: 'in-progress',
  from: 'Aéroport Lyon-Saint Exupéry',
  to: 'Val Thorens',
  date: '15 Février 2025',
  time: '14:30',
  vehicle: 'Mercedes Classe V',
  price: '320€',
  driver: {
    name: 'Pierre Dubois',
    phone: '+33 6 12 34 56 78',
    rating: 4.9,
  },
};

export default function BookingTracker() {
  const [trackingId, setTrackingId] = useState('');
  const [searchResult, setSearchResult] = useState<BookingStatus | null>(null);
  const [showDemo, setShowDemo] = useState(false);
  const [searchError, setSearchError] = useState('');

  const handleSearch = () => {
    if (!trackingId.trim()) {
      setSearchError('Veuillez entrer un numéro de réservation');
      return;
    }

    if (trackingId.toUpperCase() === DEMO_BOOKING.id) {
      setSearchResult(DEMO_BOOKING);
      setShowDemo(false);
      setSearchError('');
    } else {
      setSearchError('Réservation non trouvée. Vérifiez le numéro ou essayez l\'exemple.');
      setSearchResult(null);
    }
  };

  const getStatusSteps = (currentStatus: BookingStatus['status']) => {
    const steps = [
      { key: 'pending', label: 'Demande reçue', icon: 'ri-file-list-3-line' },
      { key: 'confirmed', label: 'Confirmée', icon: 'ri-checkbox-circle-line' },
      { key: 'assigned', label: 'Chauffeur assigné', icon: 'ri-user-star-line' },
      { key: 'in-progress', label: 'En route', icon: 'ri-car-line' },
      { key: 'completed', label: 'Terminée', icon: 'ri-flag-line' },
    ];

    const currentIndex = steps.findIndex((s) => s.key === currentStatus);

    return steps.map((step, index) => ({
      ...step,
      completed: index <= currentIndex,
      active: index === currentIndex,
    }));
  };

  const renderBookingCard = (booking: BookingStatus) => {
    const statusSteps = getStatusSteps(booking.status);

    return (
      <div className="space-y-6">
        {/* Progress Bar */}
        <div className="relative">
          <div className="flex justify-between items-start mb-4">
            {statusSteps.map((step, index) => (
              <div key={step.key} className="flex flex-col items-center flex-1 relative">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                    step.completed
                      ? 'bg-gradient-to-br from-voyage-gold to-amber-500 text-night-blue shadow-lg'
                      : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  <i className={`${step.icon} text-xl`}></i>
                </div>
                <span
                  className={`mt-2 font-inter text-xs text-center ${
                    step.completed ? 'text-night-blue font-bold' : 'text-slate-400'
                  }`}
                >
                  {step.label}
                </span>
                {index < statusSteps.length - 1 && (
                  <div
                    className={`absolute top-6 left-[calc(50%+24px)] w-[calc(100%-48px)] h-1 ${
                      step.completed ? 'bg-voyage-gold' : 'bg-slate-200'
                    }`}
                  ></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Booking Details */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
            <h4 className="font-inter font-bold text-night-blue mb-3 flex items-center gap-2">
              <i className="ri-route-line text-voyage-gold"></i>
              Trajet
            </h4>
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <i className="ri-map-pin-line text-emerald-500 mt-0.5"></i>
                <div>
                  <div className="font-inter text-xs text-slate-500">Départ</div>
                  <div className="font-inter text-sm text-night-blue font-semibold">{booking.from}</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <i className="ri-map-pin-fill text-red-500 mt-0.5"></i>
                <div>
                  <div className="font-inter text-xs text-slate-500">Arrivée</div>
                  <div className="font-inter text-sm text-night-blue font-semibold">{booking.to}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
            <h4 className="font-inter font-bold text-night-blue mb-3 flex items-center gap-2">
              <i className="ri-calendar-line text-voyage-gold"></i>
              Date & Heure
            </h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <i className="ri-calendar-check-line text-slate-400"></i>
                <span className="font-inter text-sm text-night-blue font-semibold">{booking.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-slate-400"></i>
                <span className="font-inter text-sm text-night-blue font-semibold">{booking.time}</span>
              </div>
            </div>
          </div>

          <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
            <h4 className="font-inter font-bold text-night-blue mb-3 flex items-center gap-2">
              <i className="ri-car-line text-voyage-gold"></i>
              Véhicule
            </h4>
            <div className="font-inter text-sm text-night-blue font-semibold">{booking.vehicle}</div>
            <div className="font-inter text-xs text-slate-500 mt-1">Confort premium</div>
          </div>

          <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
            <h4 className="font-inter font-bold text-night-blue mb-3 flex items-center gap-2">
              <i className="ri-price-tag-3-line text-voyage-gold"></i>
              Prix
            </h4>
            <div className="font-playfair text-2xl text-night-blue font-bold">{booking.price}</div>
            <div className="font-inter text-xs text-slate-500 mt-1">Tout compris</div>
          </div>
        </div>

        {/* Driver Info */}
        {booking.driver && (
          <div className="bg-gradient-to-r from-voyage-gold/10 to-amber-50 rounded-xl p-5 border border-voyage-gold/20">
            <h4 className="font-inter font-bold text-night-blue mb-3 flex items-center gap-2">
              <i className="ri-user-star-line text-voyage-gold"></i>
              Votre chauffeur
            </h4>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-night-blue rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-voyage-gold text-xl"></i>
                </div>
                <div>
                  <div className="font-inter font-bold text-night-blue">{booking.driver.name}</div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <i className="ri-star-fill text-amber-400 text-sm"></i>
                    <span className="font-inter text-sm text-slate-600">{booking.driver.rating}/5</span>
                  </div>
                </div>
              </div>
              <a
                href={`tel:${booking.driver.phone}`}
                className="px-5 py-2.5 bg-gradient-to-r from-voyage-gold to-amber-500 text-night-blue rounded-lg font-inter font-bold text-sm hover:shadow-lg transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-phone-line"></i>
                Appeler
              </a>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="mb-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        <div className="p-6 sm:p-8">
          <AnimatePresence mode="wait">
            {searchResult && (
              <motion.div key="result" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                {renderBookingCard(searchResult)}
              </motion.div>
            )}

            {showDemo && !searchResult && (
              <motion.div key="demo" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-blue-500 text-xl mt-0.5"></i>
                    <div>
                      <div className="font-inter font-bold text-blue-900 text-sm">Mode démonstration</div>
                      <div className="font-inter text-xs text-blue-700 mt-1">
                        Ceci est un exemple de suivi. Entrez <strong>VE-2025-4821</strong> pour voir le résultat.
                      </div>
                    </div>
                  </div>
                </div>
                {renderBookingCard(DEMO_BOOKING)}
              </motion.div>
            )}

            {!searchResult && !showDemo && (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-12"
              >
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-search-line text-slate-400 text-3xl"></i>
                </div>
                <h3 className="font-playfair text-xl font-bold text-night-blue mb-2">
                  Entrez votre numéro de réservation pour suivre votre transfert
                </h3>
                <p className="font-inter text-sm text-slate-500">Format : VE-XXXX-XXXX</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </section>
  );
}
